import { useEffect, useRef, useState } from "react";
import { LiaSearchPlusSolid, LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { categories, rawMaterials, Unit } from "../../utlis/apiService";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import Model from "../../common/Model";
import FormFields from "../../common/FormFields";
import ConfirmDeleteModal from "../../common/DeleteModel";
import ErrorModal from "../../common/CustomModel";
import { HiOutlinePlus } from "react-icons/hi";
import EntityAddModal from "../../common/EntityAddModal";
import Button from "../../common/Button";

const RawMaterial = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorSubMessage, setErrorSubMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    category_Id: "",
    unit_Id: "",
  });
  const [loading, setLoading] = useState(false);
  const [isMaterialsData, setIsMaterialsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isCategoryData, setIsCategoryData] = useState([]);
  const [isUnitData, setIsUnitData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [modalType, setModalType] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const tableScrollRef = useRef(null);

  const fetchLocationData = async () => {
    if (isFetchingRef.current) return;
    setLoading(true);
    try {
      const res = await rawMaterials.getAllRawMaterials(currentPage, pageSize);
      const newItems = res.items;

      if (currentPage === 1) {
        setIsMaterialsData(newItems);
      } else {
        setIsMaterialsData((prev) => [...prev, ...newItems]);
      }

      if (newItems.length < pageSize) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      notify("Error fetching location data", "error");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const onScroll = () => {
      if (loading || !hasMore || isFetchingRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = el;

      if (scrollHeight - scrollTop <= clientHeight + 100) {
        isFetchingRef.current = true;
        setCurrentPage((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, hasMore]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoryRes, unitRes] = await Promise.all([
        categories.getAll(),
        Unit.getAll(),
      ]);
      setIsCategoryData(categoryRes || []);
      setIsUnitData(unitRes || []);
    } catch (error) {
      notify("Error fetching location data", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLocationData();
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredData(isMaterialsData);
    } else {
      const filtered = isMaterialsData.filter((d) =>
        d.status_Name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }
  }, [searchQuery, isMaterialsData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setIsModelOpen(true);
    setIsEditing(false);
    setFormData({
      id: 0,
      name: "",
      category_Id: "",
      unit_Id: "",
    });
  };

  const handleEdit = async (id) => {
    try {
      const data = await rawMaterials.getById(id);
      if (data) {
        setFormData({
          id: data.id,
          name: data.name,
          category_Id: data.category_Id,
          unit_Id: data.unit_Id,
        });
        setIsEditing(true);
        setIsModelOpen(true);
      }
    } catch (error) {
      notify("Failed to fetch location details", "error");
    }
  };

  const handleDelete = (id) => {
    setSelectedId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await rawMaterials.delete(selectedId);
      notify("Deleted successfully", "success");
      fetchLocationData();
    } catch (error) {
      notify("Failed to delete record", "error");
    }
    setIsDeleteModalOpen(false);
    setSelectedId(null);
  };

  const handleSubmit = async ({ resetOnly = false }) => {
    try {
      await rawMaterials.saveOrUpdate(formData);
      notify(
        isEditing ? "Updated successfully" : "Added successfully",
        "success",
      );
      fetchLocationData();
      setFormData({
        id: 0,
        name: "",
        category_Id: "",
        unit_Id: "",
      });
      setIsEditing(false);
      if (!resetOnly) {
        setIsModelOpen(false);
      }
    } catch (error) {
      notify("Failed to submit form", "error");
      setErrorMessage("Submission failed");
      setErrorSubMessage("Please check your input or try again.");
      setErrorModalOpen(true);
    }
  };

  const optionMap = {
    category: {
      setter: setIsCategoryData,
      fetcher: categories.getAll,
      mapFn: (data) =>
        data.map((c) => ({
          value: c.id,
          label: c.name,
        })),
      setField: "category_Id",
    },
    unit: {
      setter: setIsUnitData,
      fetcher: Unit.getAll,
      mapFn: (data) =>
        data.map((s) => ({
          value: s.unit_Id,
          label: s.unit_Name,
        })),
      setField: "unit_Id",
    },
  };

  const handleEntityAddSuccess = async (type, newOption) => {
    const config = optionMap[type];
    if (!config) return;

    try {
      const updated = await config.fetcher();
      config.setter(updated || []);
      setFormData((prev) => ({
        ...prev,
        [config.setField]: newOption?.value || "",
      }));
      setModalType(null);
    } catch (err) {
      notify("Failed to refresh dropdown", "error");
    }
  };

  const enhancedFields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      errorMessage: "Name is required",
    },
    {
      name: "category_Id",
      label: "Category Name",
      type: "selectWithAdd",
      required: true,
      options: isCategoryData.map((s) => ({
        value: s.id,
        label: s.name,
      })),
      onAddNew: () => setModalType("category"),
      errorMessage: "Category is required",
    },
    {
      name: "unit_Id",
      label: "Unit Name",
      type: "selectWithAdd",
      options: isUnitData.map((s) => ({
        value: s.unit_Id,
        label: s.unit_Name,
      })),
      onAddNew: () => setModalType("unit"),
    },
  ];

  return (
    <>
      <div>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col">
            <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
              Die Master
            </h2>
            <p className="text-sm text-gray-500">
              Manage and track your die inventory
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <div className="relative w-full sm:w-72 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <LiaSearchSolid className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search by Die No..."
                className="block w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                >
                  <MdClose size={18} />
                </button>
              )}
            </div>

            <Button
              text="Add Raw Material"
              className="bg-orange text-black hover:bg-white font-medium px-4 py-2 rounded-lg flex items-center shadow-sm transition-all"
              icon={HiOutlinePlus}
              onClick={handleAdd}
            />
          </div>
        </div>
        <div
          ref={tableScrollRef}
          className="relative overflow-auto border border-slate-200 rounded-xl shadow-inner bg-white"
          style={{ height: "calc(100vh - 320px)" }}
        >
          <div className="min-w-max">
            {loading && <Loader />}
            <Table
              columns={[
                { key: "name", label: "Name" },
                { key: "category_Name", label: "Category Name" },
                { key: "unit_Name", label: "Unit Name" },
              ]}
              data={filteredData}
              onEdit={(row) => handleEdit(row.id)}
              onDelete={(row) => handleDelete(row.id)}
            />
          </div>
        </div>
      </div>

      <Model
        isOpen={isModelOpen}
        onClose={() => setIsModelOpen(false)}
        name={isEditing ? "Edit Raw Material" : "Add Raw Material"}
      >
        <FormFields
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: false });
          }}
          onSubmitAndAddNew={(e) => {
            e.preventDefault();
            handleSubmit({ resetOnly: true });
          }}
          setIsModelOpen={setIsModelOpen}
          formData={formData}
          handleInputChange={handleInputChange}
          fields={enhancedFields}
          isEditing={isEditing}
          onClear={() => setIsModelOpen(false)}
        />
      </Model>
      <Model
        isOpen={!!modalType}
        onClose={() => setModalType(null)}
        name={`Add ${modalType}`}
      >
        <EntityAddModal
          type={modalType}
          onClose={(newOption) => {
            setModalType(null);
            handleEntityAddSuccess(modalType, newOption);
          }}
        />
      </Model>
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {errorModalOpen && (
        <ErrorModal
          message={errorMessage}
          subMessage={errorSubMessage}
          onClose={() => setErrorModalOpen(false)}
        />
      )}
    </>
  );
};

export default RawMaterial;
